import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Route, Routes } from '@angular/router';
import { ApplicationController } from '@core/application';
import { AuthGuard } from '@core/guards';
import { List } from '@core/lang';
import { FeatureType, FeatureTypes, UserController } from '@core/user';
import { BaseMixinService } from '@mixins/services';
import { ShellComponent } from '@shell/shell.component';
import { clearTimeouts } from '@utils/clear-timeouts';
import { µsetTimeout } from '@utils/function/timer';
import { WizardBasketController } from '@wizard/services';
import { WizardPanelController } from '@wizard/wizard-panel';
import { BehaviorSubject, Observable } from 'rxjs';

const ShellFeatureList: List<FeatureType> = [
  'ReferentialCompanyBusinessCatalog',
  'ReferentialBusinessCatalogAdministration',
  'ReferentialCompanyHub',
  'ReferentialCompanyWizard',
  'ReferentialCompanyAudit',
  'ReferentialCompanyIntegrator',
  'Administration'
];

export type WizardPanelAnchorType = 'fixed' | 'floating';
export const CSS_HAS_BEEN_UPDATED_DURATION = (1000 * 1); // 1sec

@Injectable({ providedIn: 'root' })
export class ShellController extends BaseMixinService {

  static childRoutes(routes: Routes): Route {
    return {
      path: '',
      component: ShellComponent,
      children: routes,
      canActivate: [AuthGuard],
      data: { animation: 'fade' }
    };
  }

  /** @Inject */
  readonly #applicationController = inject(ApplicationController);
  readonly #userController = inject(UserController);
  readonly #wizardBasketController = inject(WizardBasketController);
  readonly #wizardPanelController = inject(WizardPanelController);

  readonly #hasFeature = new Map<FeatureType, BehaviorSubject<boolean>>();
  readonly #hasFeatureObs = new Map<FeatureType, Observable<boolean>>();

  readonly title = signal('RapidViews Platform');
  readonly isSettingsOpened = signal(false);
  readonly isNavigationExpanded = signal(false);
  readonly isWizardPanelVisible = signal(false);
  readonly isUserAdministrator = signal(false);
  readonly wizardHasBeenUpdated = signal(false);

  readonly isWizardPanelExpanded: Signal<boolean>;
  readonly hasUser: Signal<boolean>;
  readonly userFullName: Signal<string>;
  readonly wizardHasBasket: Signal<boolean>;
  readonly wizardBasketIsEmpty: Signal<boolean>;
  readonly wizardHasBeenUpdatedFromBroadcastChannel = signal(false);
  #hasBeenUpdatedTimerId: number;
  #zenMode = signal(false);
  #emphazeContent = signal(false);
  readonly isZenMode = computed(() => this.#zenMode());
  readonly isContentEmphazed = computed(() => this.#emphazeContent());

  constructor() {
    super();
    ShellFeatureList.forEach(feature => {
      this.#hasFeature.set(feature, new BehaviorSubject(false));
      this.#hasFeatureObs.set(feature, this.#hasFeature.get(feature)!.asObservable());
    });

    this.hasUser = toSignal(
      this.#userController.hasUser$,
      { initialValue: false, injector: this.injector }
    );

    this.userFullName = toSignal(
      this.#userController.observeProperty('fullName'),
      { initialValue: ' ', injector: this.injector }
    );

    this.wizardHasBasket = toSignal(
      this.#wizardBasketController.hasBasket$,
      { initialValue: false, injector: this.injector }
    );

    this.wizardBasketIsEmpty = toSignal(
      this.#wizardBasketController.isBasketEmpty$,
      { initialValue: true, injector: this.injector }
    );

    this.isWizardPanelExpanded = toSignal(
      this.#wizardPanelController.isExpanded$,
      { initialValue: false, injector: this.injector }
    );

    this.#userController.hasUser$.subscribe(this.#initialiseFeatures.bind(this));

    this.#wizardBasketController.basketChanges()
      .subscribe(event => {
        switch (event.action) {
          case 'Clear': break;
          case 'Delete': break;
          case 'Initialize': break;
          case 'New': break;
          case 'Update':
            clearTimeouts([this.#hasBeenUpdatedTimerId]);
            this.wizardHasBeenUpdatedFromBroadcastChannel.set(false);
            this.wizardHasBeenUpdated.set(true);
            this.#hasBeenUpdatedTimerId = µsetTimeout(() => this.wizardHasBeenUpdated.set(false), CSS_HAS_BEEN_UPDATED_DURATION);
            break;
          case 'UpdateFromOtherTab':
            clearTimeouts([this.#hasBeenUpdatedTimerId]);
            this.wizardHasBeenUpdated.set(false);
            this.wizardHasBeenUpdatedFromBroadcastChannel.set(true);
            this.#hasBeenUpdatedTimerId = µsetTimeout(
              () => this.wizardHasBeenUpdatedFromBroadcastChannel.set(false),
              CSS_HAS_BEEN_UPDATED_DURATION
            );
            break;
          default: break;
        }
      });
  }

  get isAdministrator(): boolean { return this.isUserAdministrator(); }
  get fullName(): string { return this.userFullName(); }

  get zenMode(): boolean { return this.#zenMode(); }
  set zenMode(value: boolean) { this.#zenMode.set(value); }

  get emphazeContent(): boolean { return this.#emphazeContent(); }
  set emphazeContent(value: boolean) { this.#emphazeContent.set(value); }

  logout() {
    this.#applicationController.logout();
  }

  observeFeature(feature: FeatureType): Observable<boolean> {
    return this.#hasFeatureObs.get(feature)!;
  }

  hasFeature(feature: FeatureType): boolean {
    return this.#hasFeature.get(feature)?.getValue();
  }

  toggleNavigationExpand(): ShellController {
    const state = this.isNavigationExpanded();
    this.isNavigationExpanded.set(!state);
    return this;
  }

  changeTitle(title: string): ShellController {
    this.title.set(title);
    return this;
  }

  openSettings(): ShellController {
    this.isSettingsOpened.set(true);
    return this;
  }

  closeSettings(): ShellController {
    this.isSettingsOpened.set(false);
    return this;
  }

  displayWizardPanel(): ShellController {
    if (this.hasFeature(FeatureTypes.ReferentialCompanyWizard)) {
      this.isWizardPanelVisible.set(true);
    }
    return this;
  }

  removeWizardPanel(): ShellController {
    this.isWizardPanelVisible.set(false);
    return this;
  }

  openWizardPanel(): ShellController {
    if (this.hasFeature(FeatureTypes.ReferentialCompanyWizard)) {
      this.#wizardPanelController.openWizardPanel();
    }
    return this;
  }

  openOrCloseWizardPanel(): ShellController {
    this.#wizardPanelController.openOrCloseWizardPanel();
    return this;
  }

  closeWizardPanel(): ShellController {
    this.#wizardPanelController.closeWizardPanel();
    return this;
  }

  toggleWizardPanel(force = false): ShellController {
    if (this.hasFeature(FeatureTypes.ReferentialCompanyWizard) || force) {
      this.#wizardPanelController.toggleWizardPanel();
    }
    return this;
  }

  #initialiseFeatures(hasUser: boolean) {
    ShellFeatureList.forEach(feature => this.#hasFeature.get(feature)!.next(hasUser && this.#userController.hasFeature(feature)));
    this.isUserAdministrator.set(this.#userController.hasFeature(FeatureTypes.Administration));
  }
}
